import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import type { AthenaEvent } from "@evidenceb/athena-event-storage-schemas";
import pRetry from "p-retry";
import { lazy, Suspense, useContext, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import Dev from "../components/Dev/Dev";
import ErrorModal from "../components/ErrorModal/ErrorModal";
import Loader from "../components/Loader/Loader";
import appConfig from "../config";
import { blockingLoaderStore } from "../contexts/BlockingLoaderContext";
import { configStore } from "../contexts/ConfigContext";
import { errorStore } from "../contexts/ErrorContext";
import { useInitializingEventStream } from "../contexts/EventStreamContext";
import { sessionStore } from "../contexts/SessionContext";
import useAthenaAPIClient from "../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import { useUserExtra } from "../hooks/queries/useUserExtra";
import { PLACEHOLDER_SUBJECT, useSubjects } from "../hooks/useActiveSubject";
import useInitApp from "../hooks/useInitApp/useInitApp";
import useOnlineStatus from "../hooks/useOnlineStatus";
import { usePingEvent } from "../hooks/usePingEvent";
import { useUserType } from "../hooks/useUserInfo";
import { AuthMode } from "../interfaces/Config";
import { LoaderStatus } from "../interfaces/Status";
import { UserType } from "../interfaces/User";
import Account from "../pages/Account/Account";
import { useAccountSections } from "../pages/Account/utils";
import { BASE_ROUTE as AUTH_ROUTE } from "../pages/Authentication/routes";
import ErrorComponent from "../pages/Error/Error";
import { getPlatformDetails } from "../utils/platform";
import { makeLoggedInStatement } from "../utils/statements/statement-builder";
import { setActiveSubjectCSSVariablesOnBody } from "../utils/styles";
import { ROUTES } from "./routing";

import "react-toastify/dist/ReactToastify.css";
import "./Root.scss";
import UserTypeSwitch from "../components/Header/UserTypeSwitch/UserTypeSwitch";
import useBreakpoints from "../hooks/useBreakpoints";
import Preview from "../pages/Authentication/Preview/Preview";
import { isSpecimenVersion } from "../utils/init";

const StudentRouter = lazy(() => import("./StudentRouter"));
const TeacherRouter = lazy(() => import("./TeacherRouter"));
const Authentication = lazy(
  () => import("../pages/Authentication/Authentication"),
);

function Root() {
  const { config } = useContext(configStore);
  const { errorInfo } = useContext(errorStore);
  const { isBlocking } = useContext(blockingLoaderStore);
  const { status, setStatus } = useInitApp();
  const userType = useUserType();
  const intl = useIntl();
  const accountSections = useAccountSections();
  const showAccount = Object.values(accountSections).some((show) => show);

  useEffect(() => {
    if (errorInfo.page) setStatus(LoaderStatus.Error);
  }, [errorInfo, setStatus]);

  useSendLoginStatement(status);
  usePingEvent();

  useStylesSetup();

  const urlParams = new URLSearchParams(location.search);
  const displayParam = urlParams.get("display");
  const { isMobile } = useBreakpoints();
  return (
    <Router basename={appConfig.basePath}>
      <Suspense fallback={<Loader isLoading />}>
        <div className="app-root">
          {userType === UserType.Student && (
            <StudentRouter isLoading={status !== LoaderStatus.Success} />
          )}
          {userType === UserType.Teacher && (
            <TeacherRouter isLoading={status !== LoaderStatus.Success} />
          )}

          <Loader
            isLoading={status === LoaderStatus.Loading || isBlocking}
            loadingMessage={intl.formatMessage(messages.loadingApplication)}
            className="app-root"
          >
            {status === LoaderStatus.Success ? (
              <>
                <Dev />
                <Switch>
                  <Route path={`/${AUTH_ROUTE}/`}>
                    <Authentication />
                  </Route>
                  {config.auth.mode === AuthMode.Authentication &&
                    (displayParam ? (
                      <Route path={`/${ROUTES.preview}`}>
                        <Preview displayParam={displayParam} />
                      </Route>
                    ) : (
                      <Redirect to={`/${AUTH_ROUTE}/`} />
                    ))}

                  <Route path="/error">
                    <ErrorComponent />
                  </Route>

                  {showAccount && (
                    <Route path={`/${ROUTES.account}`}>
                      <Account />
                    </Route>
                  )}
                </Switch>
                {isMobile && isSpecimenVersion() && (
                  <UserTypeSwitch className="header__user-switch" />
                )}
                {isBlocking && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 9000,
                      background: "rgba(0, 0, 0, 0.2)",
                      cursor: "wait",
                    }}
                  />
                )}
              </>
            ) : status === LoaderStatus.Error ? (
              errorInfo.page && <ErrorComponent />
            ) : null}
          </Loader>
        </div>

        {/** Init Error modal */}
        <ErrorModal />

        <div>
          <ToastContainer
            className="toast-action-feedback"
            enableMultiContainer
            containerId={"action-feedback"}
            position={toast.POSITION.BOTTOM_CENTER}
            transition={Bounce}
            closeButton={
              <button>
                <Icon path="close" color="var(--text-main-default)" />
              </button>
            }
            hideProgressBar
          />
          <ToastContainer
            className="toast-progress-notifier"
            enableMultiContainer
            containerId={"progress-notifier"}
            position={toast.POSITION.TOP_RIGHT}
            transition={Bounce}
            theme="light"
          />
          <ToastContainer
            className="toast-athena-event-saving-failure-notifier"
            enableMultiContainer
            containerId={"athena-event-saving-failure-notifier"}
            position={toast.POSITION.BOTTOM_CENTER}
            transition={Bounce}
            theme="light"
          />
          <ToastContainer
            className="toast-specimen-user-switch"
            enableMultiContainer
            containerId={"specimen-user-switch"}
            position={toast.POSITION.TOP_RIGHT}
            transition={Bounce}
            theme="light"
          />
        </div>
      </Suspense>
    </Router>
  );
}
export default Root;

const messages = defineMessages({
  loadingApplication: {
    id: "loadingApp",
    defaultMessage: "You are loading an EvidenceB application. Please wait.",
  },
});

const useSendLoginStatement = (status: LoaderStatus | undefined) => {
  const {
    session: { evidencebId, sessionId },
  } = useContext(sessionStore);
  const { session } = useContext(sessionStore);
  const athenaAPIClient = useAthenaAPIClient();
  const { config } = useContext(configStore);
  const isOnline = useOnlineStatus();
  const athenaEventStream = useInitializingEventStream();

  useEffect(() => {
    if (
      !athenaEventStream ||
      status !== LoaderStatus.Success ||
      !config.features?.additionalStatements?.studentLogIn
    )
      return;
    const statement = makeLoggedInStatement(
      config.declinaison,
      evidencebId,
      sessionId,
    );
    pRetry(
      () => {
        return athenaAPIClient.postStatement(statement);
      },
      { retries: 5 },
    );

    const { browserName, fullVersion, OSName, isTouchDevice } =
      getPlatformDetails();

    const event: AthenaEvent<"session", "SessionLoginEvent"> = {
      id: uuid(),
      createdAt: new Date().toISOString(),
      userId: evidencebId,
      sessionId: sessionId,
      app: session.version,
      categoryData: {
        online: isOnline,
      },
      type: "SessionLoginEvent",
      data: {
        app: session.version ?? "athena", // get app in format 'athena/variation/branch/version'
        browser: {
          name: browserName,
          version: fullVersion,
        },
        lang: navigator.language,
        os: {
          name: OSName,
          version: "-1",
        },
        touchDevice: isTouchDevice,
      },
    };
    athenaEventStream.publish(event);
  }, [
    status,
    config.declinaison,
    evidencebId,
    session.version,
    sessionId,
    athenaEventStream,
    athenaAPIClient,
    config.features,
    isOnline,
  ]);
};

const useStylesSetup = () => {
  const subjects = useSubjects();
  const [extra] = useUserExtra();
  useEffect(() => {
    if (extra.activeSubject && subjects[0].id !== PLACEHOLDER_SUBJECT.id) {
      const activeSubjectObj = subjects.find(
        (subject) => subject.id === extra.activeSubject,
      );
      setActiveSubjectCSSVariablesOnBody(activeSubjectObj);
    }
  }, [extra.activeSubject, subjects]);

  // On body rather than the app root so that portals (modals, toasts) get the
  // kid friendly tokens too
  const isKidFriendly = useFeatureFlag("kidFriendlyInterface");
  const isKidFriendlyYellow = useFeatureFlag("kidFriendlyYellowTheme");
  useEffect(() => {
    document.body.classList.toggle("app--kid-friendly", isKidFriendly);
    document.body.classList.toggle(
      "app--kid-friendly-yellow",
      isKidFriendly && isKidFriendlyYellow,
    );
  }, [isKidFriendly, isKidFriendlyYellow]);
};
